import React, { FormEvent, useCallback, useMemo, useState } from 'react';

import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import { OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE_HOTEL, OFFER_TYPE_TACTICAL_AO_HOTEL } from '~/consts/offerTypes';
import type { TagType } from '~/consts/tags';
import { TAG_TYPE_CAMPAIGN } from '~/consts/tags';

import OffersService from '~/services/OffersService';

type BulkActionMode = 'apply' | 'remove';

type BulkActionSuccessEntry = {
  offerId: string;
  changedTags: Array<string>;
  skippedTags: Array<string>;
};

type BulkActionResult = {
  mode: BulkActionMode;
  success: Array<BulkActionSuccessEntry>;
  unchanged: Array<string>;
  failures: Array<{ offerId: string; reason: string }>;
};

type Props = {
  tags: Array<App.MarketingTag>;
  tagType: TagType;
};

type TypeConfig = {
  title: string;
  labelSingular: string;
  labelPlural: string;
  fieldName: 'campaigns' | 'holiday_types';
  helperText: string;
  offerIdPlaceholder: string;
  eligibleOfferTypes?: Array<string>;
  ineligibleReason?: (offerType: string) => string;
};

const FLASH_OFFER_TYPES = [OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE_HOTEL, OFFER_TYPE_TACTICAL_AO_HOTEL];

function parseOfferIds(rawIds: string) {
  return Array.from(
    new Set(
      rawIds
        .split(/[\s,]+/)
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  );
}

function titleCase(value: string) {
  return value.replace(/^\w/, (char) => char.toUpperCase());
}

function getConfig(tagType: TagType): TypeConfig {
  if (tagType === TAG_TYPE_CAMPAIGN) {
    return {
      title: 'Bulk apply campaign tags to flash offers',
      labelSingular: 'campaign tag',
      labelPlural: 'campaign tags',
      fieldName: 'campaigns',
      helperText: 'Select one or more campaign tags above. Enter flash offer IDs separated by commas or new lines.',
      offerIdPlaceholder: 'Enter offer IDs separated by commas or new lines (e.g. OFF123, OFF456)',
      eligibleOfferTypes: FLASH_OFFER_TYPES,
      ineligibleReason: (offerType) => `Offer type "${offerType}" is not eligible for flash campaign tagging.`,
    };
  }

  return {
    title: 'Bulk apply holiday types to offers',
    labelSingular: 'holiday type',
    labelPlural: 'holiday types',
    fieldName: 'holiday_types',
    helperText: 'Select one or more holiday types above. Enter offer IDs separated by commas or new lines.',
    offerIdPlaceholder: 'Enter offer IDs separated by commas or new lines (e.g. OFF123, OFF456)',
  };
}

export default function BulkTagApply({ tags, tagType }: Props) {
  const [selectedTags, setSelectedTags] = useState<Array<App.MarketingTag>>([]);
  const [offerIdsInput, setOfferIdsInput] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<BulkActionResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const config = useMemo(() => getConfig(tagType), [tagType]);
  const tagOptions = useMemo(() => [...tags].sort((a, b) => a.name.localeCompare(b.name)), [tags]);
  const tagNameLookup = useMemo(() => {
    const lookup = new Map<string, App.MarketingTag>();
    tagOptions.forEach((tag) => {
      lookup.set(tag.name.toLocaleLowerCase(), tag);
    });
    return lookup;
  }, [tagOptions]);

  const handleTagPaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const pastedText = event.clipboardData?.getData('text') ?? '';

      if (!pastedText || !/[\n,\r]/.test(pastedText)) {
        return;
      }

      const pastedNames = pastedText
        .split(/\r?\n|,/)
        .map((value) => value.trim())
        .filter(Boolean);

      if (!pastedNames.length) {
        return;
      }

      let shouldPreventDefault = false;

      setSelectedTags((previous) => {
        const existingIds = new Set(previous.map((tag) => tag.id));
        const appendedTags: Array<App.MarketingTag> = [];

        pastedNames.forEach((name) => {
          const lookupKey = name.toLocaleLowerCase();
          const matchedTag = tagNameLookup.get(lookupKey);

          if (!matchedTag || existingIds.has(matchedTag.id)) {
            return;
          }

          appendedTags.push(matchedTag);
          existingIds.add(matchedTag.id);
        });

        if (!appendedTags.length) {
          return previous;
        }

        shouldPreventDefault = true;
        return [...previous, ...appendedTags];
      });

      if (shouldPreventDefault) {
        event.preventDefault();
      }
    },
    [tagNameLookup],
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await runBulkAction('apply');
  };

  const runBulkAction = async (mode: BulkActionMode) => {
    if (isSubmitting) {
      return;
    }

    if (!selectedTags.length) {
      setFormError(
        `Select at least one ${mode === 'apply' ? config.labelSingular : config.labelPlural} to ${
          mode === 'apply' ? 'apply' : 'remove'
        }.`,
      );
      return;
    }

    const offerIds = parseOfferIds(offerIdsInput);

    if (!offerIds.length) {
      setFormError('Enter at least one offer ID.');
      return;
    }

    setFormError(null);
    setIsSubmitting(true);
    setResult(null);

    const successes: Array<BulkActionSuccessEntry> = [];
    const unchanged: Array<string> = [];
    const failures: Array<{ offerId: string; reason: string }> = [];
    const selectedTagNames = selectedTags.map((tag) => tag.name);

    try {
      for (const offerId of offerIds) {
        try {
          const offerResponse = await OffersService.getOffer(offerId);
          const offer = offerResponse?.result as App.Offer | undefined;

          if (!offer) {
            failures.push({
              offerId,
              reason: 'Offer not found.',
            });
            continue;
          }

          if (config.eligibleOfferTypes && !config.eligibleOfferTypes.includes(offer.type)) {
            failures.push({
              offerId,
              reason: config.ineligibleReason ? config.ineligibleReason(offer.type) : 'Offer type not eligible.',
            });
            continue;
          }

          const currentTags = Array.isArray(offer[config.fieldName]) ? (offer[config.fieldName] as Array<string>) : [];
          const presentTags = selectedTagNames.filter((tagName) => currentTags.includes(tagName));
          const missingTags = selectedTagNames.filter((tagName) => !currentTags.includes(tagName));

          if (mode === 'apply') {
            if (!missingTags.length) {
              unchanged.push(offerId);
              continue;
            }

            const updatedTags = [...currentTags, ...missingTags];
            await OffersService.updateOffer({ type: offer.type, [config.fieldName]: updatedTags }, offerId);
            successes.push({
              offerId,
              changedTags: missingTags,
              skippedTags: presentTags,
            });
            continue;
          }

          // remove mode
          if (!presentTags.length) {
            unchanged.push(offerId);
            continue;
          }

          const updatedTags = currentTags.filter((tag) => !selectedTagNames.includes(tag));
          await OffersService.updateOffer({ type: offer.type, [config.fieldName]: updatedTags }, offerId);
          successes.push({
            offerId,
            changedTags: presentTags,
            skippedTags: missingTags,
          });
        } catch (error: unknown) {
          const reason = error instanceof Error ? error.message : 'Unknown error occurred.';
          failures.push({
            offerId,
            reason,
          });
        }
      }
    } finally {
      setResult({
        mode,
        success: successes,
        unchanged,
        failures,
      });
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="section">
      <Typography variant="h6" mb={2}>
        {config.title}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <Autocomplete
            multiple
            options={tagOptions}
            getOptionLabel={(option) => option.name}
            value={selectedTags}
            onChange={(_, value) => setSelectedTags(value)}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) {
                return option === value;
              }
              return option.id === value.id;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={titleCase(config.labelPlural)}
                placeholder={`Select ${config.labelPlural}`}
                inputProps={{
                  ...params.inputProps,
                  onPaste: (event) => {
                    handleTagPaste(event);
                    if (!event.defaultPrevented) {
                      params.inputProps?.onPaste?.(event as React.ClipboardEvent<any>);
                    }
                  },
                }}
              />
            )}
            disabled={isSubmitting}
            limitTags={4}
          />

          <TextField
            label="Offer IDs"
            placeholder={config.offerIdPlaceholder}
            helperText={config.helperText}
            value={offerIdsInput}
            onChange={(event) => setOfferIdsInput(event.target.value)}
            multiline
            minRows={3}
            disabled={isSubmitting}
            required
          />

          {formError && (
            <Alert severity="error" onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Applying…' : 'Apply tags'}
            </Button>
            <Button
              type="button"
              variant="contained"
              color="error"
              disabled={isSubmitting}
              onClick={() => runBulkAction('remove')}
            >
              {isSubmitting ? 'Working…' : 'Remove tags'}
            </Button>
            <Button
              type="button"
              variant="text"
              disabled={isSubmitting}
              onClick={() => {
                setSelectedTags([]);
                setOfferIdsInput('');
                setResult(null);
                setFormError(null);
              }}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </form>

      {result && (
        <Box mt={3}>
          <Stack spacing={2}>
            <Alert severity="success">
              {(() => {
                const totalChangedTags = result.success.reduce((sum, entry) => sum + entry.changedTags.length, 0);
                const tagLabel = totalChangedTags === 1 ? config.labelSingular : config.labelPlural;
                const offerLabel = result.success.length === 1 ? 'offer' : 'offers';
                const verb = result.mode === 'apply' ? 'Applied' : 'Removed';
                const preposition = result.mode === 'apply' ? 'across' : 'from';
                return `${verb} ${totalChangedTags} ${tagLabel} ${preposition} ${result.success.length} ${offerLabel}.`;
              })()}
              {result.success.length > 0 && (
                <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
                  {result.success.map((entry) => (
                    <li key={entry.offerId}>
                      <Typography component="span" variant="body2">
                        {entry.offerId} –{' '}
                        {result.mode === 'apply'
                          ? `added ${entry.changedTags.join(', ')}`
                          : `removed ${entry.changedTags.join(', ')}`}
                        {entry.skippedTags.length > 0 &&
                          ` (${
                            result.mode === 'apply'
                              ? `already had ${entry.skippedTags.join(', ')}`
                              : `did not have ${entry.skippedTags.join(', ')}`
                          })`}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
              {result.unchanged.length > 0 && (
                <>
                  {' '}
                  {result.unchanged.length} {result.unchanged.length === 1 ? 'offer' : 'offers'}{' '}
                  {result.mode === 'apply' ? 'already had all selected' : 'did not have selected'} {config.labelPlural}.
                </>
              )}
            </Alert>

            {result.failures.length > 0 && (
              <Alert severity="error">
                Failed to update {result.failures.length} offer{result.failures.length === 1 ? '' : 's'}:
                <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
                  {result.failures.map((failure) => (
                    <li key={failure.offerId}>
                      <Typography component="span" variant="body2">
                        {failure.offerId} – {failure.reason}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
